import {
  DischargeInstruction,
  DischargeInstructionData,
  DISCHARGE_INSTRUCTION_VERSION,
  errorResult,
  normalizeDischargeInstruction,
  Result,
  Source,
  successResult,
  tu,
} from "beitary-shared";
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  Unsubscribe,
  updateDoc,
  writeBatch,
} from "firebase/firestore";

// add organization consultation

interface AddDischargeInstruction {
  ({
    db,
    organizationId,
    consultationId,
    authorId,
    authorName,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    authorId: string;
    authorName: string;
    source: Source;
    data: DischargeInstructionData;
  }): Promise<Result<DischargeInstruction | null>>;
}

const addDischargeInstruction: AddDischargeInstruction = async ({
  db,
  organizationId,
  consultationId,
  authorId,
  authorName,
  source,
  data,
}) => {
  try {
    // create a new ref to get a new consultation id
    const newRef = doc(
      collection(
        db,
        "organizations",
        organizationId,
        "consultations",
        consultationId,
        "discharge_instructions"
      )
    );

    const newObj: DischargeInstruction = normalizeDischargeInstruction({
      ...data,
      id: newRef.id,
      authorId,
      authorName,
      version: DISCHARGE_INSTRUCTION_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    });

    await setDoc(newRef, newObj);

    // t("DISCHARGE_INSTRUCTION_CREATED")
    const successMessage = "DISCHARGE_INSTRUCTION_CREATED";
    return successResult({
      message: successMessage,
      payload: newObj,
    });
  } catch (err: any) {
    return errorResult({ message: err.message });
  }
};

interface AddDischargeInstructions {
  ({
    db,
    organizationId,
    consultationId,
    authorId,
    authorName,
    source,
    dataArr,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    authorId: string;
    authorName: string;
    source: Source;
    dataArr: (DischargeInstructionData & { id: string })[];
  }): Promise<Result<(DischargeInstructionData & { id: string })[] | null>>;
}

const addDischargeInstructions: AddDischargeInstructions = async ({
  db,
  organizationId,
  consultationId,
  authorId,
  authorName,
  source,
  dataArr,
}) => {
  try {
    const batch = writeBatch(db);

    dataArr.forEach((data) => {
      const newRef = doc(
        db,
        "organizations",
        organizationId,
        "consultations",
        consultationId,
        "discharge_instructions",
        data.id
      );
      const newObj: DischargeInstruction = normalizeDischargeInstruction({
        ...data,
        id: newRef.id,
        authorId,
        authorName,
        version: DISCHARGE_INSTRUCTION_VERSION,
        source,
        createdAt: tu.getCurrentDateTime(),
        lastUpdatedAt: tu.getCurrentDateTime(),
      });

      batch.set(newRef, newObj);
    });

    await batch.commit();

    // t("DISCHARGE_INSTRUCTION_CREATED")
    const successMessage = "DISCHARGE_INSTRUCTION_CREATED";
    return successResult({
      message: successMessage,
      payload: dataArr,
    });
  } catch (err: any) {
    return errorResult({ message: err.message });
  }
};

interface DeleteDischargeInstructions {
  ({
    db,
    organizationId,
    consultationId,
    id,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    id: string;
  }): Promise<Result<boolean | null>>;
}

const deleteDischargeInstructions: DeleteDischargeInstructions = async ({
  db,
  organizationId,
  consultationId,
  id,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "consultations",
      consultationId,
      "discharge_instructions",
      id
    );

    try {
      await deleteDoc(docRef);
      // t("DISCHARGE_INSTRUCTIONS_DELETED")
      const successMessage = "DISCHARGE_INSTRUCTIONS_DELETED";
      return successResult({
        message: successMessage,
        payload: true,
      });
    } catch (error: any) {
      console.log(error);
      return errorResult({ message: error.message });
    }
  } catch (err: any) {
    console.log(err);
    return errorResult({ message: err.message });
  }
};

interface UpdateDischargeInstruction {
  ({
    db,
    organizationId,
    consultationId,
    authorId,
    authorName,
    id,
    source,
    data,
  }: {
    db: Firestore;
    organizationId: string;
    consultationId: string;
    authorId: string;
    authorName: string;
    id: string;
    source: Source;
    data: Partial<DischargeInstructionData>;
  }): Promise<Result<Partial<DischargeInstructionData> | null>>;
}

const updateDischargeInstruction: UpdateDischargeInstruction = async ({
  db,
  organizationId,
  consultationId,
  authorId,
  authorName,
  id,
  source,
  data,
}) => {
  try {
    const docRef = doc(
      db,
      "organizations",
      organizationId,
      "consultations",
      consultationId,
      "discharge_instructions",
      id
    );

    const updates: Partial<DischargeInstruction> = {
      ...data,
      authorId,
      authorName,
      version: DISCHARGE_INSTRUCTION_VERSION,
      source,
      createdAt: tu.getCurrentDateTime(),
      lastUpdatedAt: tu.getCurrentDateTime(),
    };

    await updateDoc(docRef, updates);

    // t("DISCHARGE_INSTRUCTION_UPDATED")
    const successMessage = "DISCHARGE_INSTRUCTION_UPDATED";

    return successResult({
      message: successMessage,
      payload: updates,
    });
  } catch (err: any) {
    console.log(err.message);
    return errorResult({ message: err.message });
  }
};

// get organization dischargeInstructions listener
interface GetDischargeInstructionsListenerCallback {
  (dischargeInstructions: DischargeInstruction[]): void;
}
interface GetDischargeInstructionsListener {
  db: Firestore;
  organizationId: string;
  consultationId: string;
  callback: GetDischargeInstructionsListenerCallback;
}

const getDischargeInstructionsListener = ({
  db,
  organizationId,
  consultationId,
  callback,
}: GetDischargeInstructionsListener): Unsubscribe => {
  try {
    // console.log("getDischargeInstructionsListener: new listener");
    const newQuery = query(
      collection(
        db,
        "organizations",
        organizationId,
        "consultations",
        consultationId,
        "discharge_instructions"
      ),
      orderBy("lastUpdatedAt", "desc")
    );
    return onSnapshot(newQuery, (querySnapshot) => {
      const dischargeInstructions: DischargeInstruction[] = [];
      querySnapshot.forEach((doc) => {
        try {
          dischargeInstructions.push(normalizeDischargeInstruction(doc.data()));
        } catch (err) {
          console.log(err);
        }
      });
      callback(dischargeInstructions);
    });
  } catch (err: any) {
    console.log(err);
    return () => {};
  }
};

// we inject dependencies to improve testability
export const dischargeInstructions = ({
  authorId,
  authorName,
  consultationId,
  db,
  organizationId,
  source,
}: {
  db: Firestore;
  organizationId: string;
  consultationId: string;
  authorId: string;
  authorName: string;
  source: Source;
}) => {
  return {
    getDischargeInstructionsListener: (
      callback: GetDischargeInstructionsListenerCallback
    ) =>
      getDischargeInstructionsListener({
        db,
        organizationId,
        consultationId,
        callback,
      }),
    addDischargeInstruction: (data: DischargeInstructionData) =>
      addDischargeInstruction({
        db,
        organizationId,
        consultationId,
        authorId,
        authorName,
        source,
        data,
      }),
    addDischargeInstructions: (
      dataArr: (DischargeInstructionData & { id: string })[]
    ) =>
      addDischargeInstructions({
        db,
        organizationId,
        consultationId,
        authorId,
        authorName,
        source,
        dataArr,
      }),
    updateDischargeInstruction: (
      id: string,
      data: Partial<DischargeInstructionData>
    ) =>
      updateDischargeInstruction({
        db,
        organizationId,
        consultationId,
        authorId,
        authorName,
        id,
        source,
        data,
      }),
    deleteDischargeInstruction: (id: string) =>
      deleteDischargeInstructions({
        db,
        organizationId,
        consultationId,
        id,
      }),
  };
};
